import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  Calculator,
  CheckCircle2,
  Info,
  Lock,
  Phone,
  Ruler,
  Square,
  X,
} from "lucide-react";
import { PRICING, ROUND_TO } from "../config/pricing";
import { BUSINESS } from "../config/business";
import { trackEvent } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LEAD_KEY = "woodstone_calc_lead";

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
});

function roundTo(n, step = ROUND_TO) {
  return Math.round(n / step) * step;
}

// Small internal helper — ToggleButton (radio-style)
function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={[
        "px-4 py-2.5 rounded-full text-sm font-medium transition-all border",
        active
          ? "bg-[#7A2D2F] text-white border-[#7A2D2F] shadow-sm"
          : "bg-white text-[#1A1A1A] border-[#DCD7CE] hover:border-[#7A2D2F] hover:text-[#7A2D2F]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function FieldLabel({ children, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[13px] font-medium text-[#1A1A1A] mb-2 tracking-wide"
    >
      {children}
    </label>
  );
}

const inputCls =
  "w-full bg-[#F8F7F4] border border-[#DCD7CE] rounded-xl px-4 py-3 text-[#1A1A1A] placeholder:text-[#9A9A90] focus:ring-2 focus:ring-[#7A2D2F]/30 focus:border-[#7A2D2F] focus:outline-none transition-all";

export default function DeckCalculator() {
  const [material, setMaterial] = useState("composite");
  const [mode, setMode] = useState("lw"); // "lw" | "area"
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [area, setArea] = useState("");
  const [customWork, setCustomWork] = useState(false);
  const [error, setError] = useState("");

  // Lead-gate state
  const [revealed, setRevealed] = useState(false); // whether the result is shown
  const [showLeadGate, setShowLeadGate] = useState(false);
  const [leadInfo, setLeadInfo] = useState(null); // {name, phone, email}
  const [leadForm, setLeadForm] = useState({ name: "", phone: "", email: "" });
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadError, setLeadError] = useState("");

  // Restore captured lead from localStorage so a returning visitor doesn't
  // need to re-enter their details for every calculation
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LEAD_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.email && parsed?.phone) setLeadInfo(parsed);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const computed = useMemo(() => {
    let a = 0;
    if (mode === "lw") {
      const l = parseFloat(length);
      const w = parseFloat(width);
      if (!isFinite(l) || !isFinite(w) || l <= 0 || w <= 0) return null;
      a = l * w;
    } else {
      const x = parseFloat(area);
      if (!isFinite(x) || x <= 0) return null;
      a = x;
    }
    if (a < 1) return null;

    const rate = PRICING[material];
    const low = roundTo(a * rate.minPerSqm);
    const high = roundTo(a * rate.maxPerSqm);
    return { area: a, low, high };
  }, [mode, length, width, area, material]);

  function onCompute() {
    if (!computed) {
      setError(
        "Please enter valid measurements to see an estimated price range."
      );
      return;
    }
    setError("");

    if (leadInfo) {
      // Already captured — reveal immediately
      setRevealed(true);
      trackEvent("calculator_used", "/decking-cost-calculator-melbourne", {
        material,
        area: computed.area,
        custom_work: customWork,
      });
      requestAnimationFrame(() => {
        const el = document.getElementById("calc-result");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
      return;
    }

    // Otherwise — open the lead-capture modal
    setLeadError("");
    setShowLeadGate(true);
  }

  async function submitLead(e) {
    e.preventDefault();
    setLeadError("");
    const f = leadForm;
    if (!f.name.trim()) return setLeadError("Please enter your name.");
    if (!f.phone.trim()) return setLeadError("Please enter your phone number.");
    if (!/^\S+@\S+\.\S+$/.test(f.email))
      return setLeadError("Please enter a valid email address.");

    setLeadLoading(true);
    try {
      const calcContext = computed
        ? {
            material,
            material_label: PRICING[material].label,
            measurement_mode: mode,
            length: mode === "lw" ? parseFloat(length) : null,
            width: mode === "lw" ? parseFloat(width) : null,
            area_sqm: computed.area,
            estimate_low: computed.low,
            estimate_high: computed.high,
            custom_work: customWork,
            source: "calculator",
          }
        : null;

      await axios.post(`${API}/quotes`, {
        name: f.name.trim(),
        phone: f.phone.trim(),
        email: f.email.trim(),
        suburb: "",
        service: `Calculator estimate – ${PRICING[material].label}`,
        message: calcContext
          ? `Auto-generated from cost calculator. Area: ${calcContext.area_sqm} m². Estimate: $${calcContext.estimate_low}–$${calcContext.estimate_high}.${
              customWork ? " Steps / custom work: yes." : ""
            }`
          : "Auto-generated from cost calculator.",
        calc_context: calcContext,
      });

      const captured = {
        name: f.name.trim(),
        phone: f.phone.trim(),
        email: f.email.trim(),
        at: new Date().toISOString(),
      };
      try {
        localStorage.setItem(LEAD_KEY, JSON.stringify(captured));
      } catch {
        /* ignore */
      }
      setLeadInfo(captured);
      setShowLeadGate(false);
      setRevealed(true);

      trackEvent("calculator_lead", "/decking-cost-calculator-melbourne", {
        material,
        area: computed?.area,
        estimate_low: computed?.low,
        estimate_high: computed?.high,
        custom_work: customWork,
      });
      trackEvent("calculator_used", "/decking-cost-calculator-melbourne", {
        material,
        area: computed?.area,
        custom_work: customWork,
      });

      requestAnimationFrame(() => {
        const el = document.getElementById("calc-result");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    } catch (err) {
      console.error(err);
      setLeadError(
        err?.response?.data?.detail ||
          "Couldn’t send your details. Please try again or call us directly."
      );
    } finally {
      setLeadLoading(false);
    }
  }

  return (
    <div
      className="bg-white rounded-3xl border border-[#DCD7CE] shadow-[0_20px_50px_rgba(20,40,31,0.08)] overflow-hidden"
      data-testid="deck-calculator"
    >
      <div className="grid lg:grid-cols-5">
        {/* Inputs */}
        <div className="lg:col-span-3 p-7 md:p-10">
          <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.25em] text-[#C9A876] mb-4 font-semibold">
            <Calculator size={14} />
            Deck cost calculator
          </div>
          <h2
            className="text-2xl md:text-3xl text-[#1A1A1A] tracking-tight leading-tight mb-7"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            Tell us about your deck.
          </h2>

          {/* Material */}
          <div className="mb-6">
            <FieldLabel>1. Deck material</FieldLabel>
            <div className="flex flex-wrap gap-3">
              <Chip
                active={material === "composite"}
                onClick={() => setMaterial("composite")}
                testid="material-composite"
              >
                Composite decking
              </Chip>
              <Chip
                active={material === "merbau"}
                onClick={() => setMaterial("merbau")}
                testid="material-merbau"
              >
                Merbau decking
              </Chip>
            </div>
          </div>

          {/* Measurement mode */}
          <div className="mb-6">
            <FieldLabel>2. How do you want to measure?</FieldLabel>
            <div className="flex flex-wrap gap-3">
              <Chip
                active={mode === "lw"}
                onClick={() => setMode("lw")}
                testid="mode-lw"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Ruler size={14} />
                  Length × Width
                </span>
              </Chip>
              <Chip
                active={mode === "area"}
                onClick={() => setMode("area")}
                testid="mode-area"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Square size={14} />
                  Total m²
                </span>
              </Chip>
            </div>
          </div>

          {/* Measurement inputs */}
          <div className="mb-6">
            {mode === "lw" ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel htmlFor="len">Length (m)</FieldLabel>
                  <input
                    id="len"
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    min="0"
                    value={length}
                    onChange={(e) => setLength(e.target.value)}
                    placeholder="e.g. 6"
                    className={inputCls}
                    data-testid="input-length"
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="wid">Width (m)</FieldLabel>
                  <input
                    id="wid"
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    min="0"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    placeholder="e.g. 4"
                    className={inputCls}
                    data-testid="input-width"
                  />
                </div>
              </div>
            ) : (
              <div>
                <FieldLabel htmlFor="area">Total area (m²)</FieldLabel>
                <input
                  id="area"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  placeholder="e.g. 24"
                  className={inputCls}
                  data-testid="input-area"
                />
              </div>
            )}
          </div>

          {/* Custom work */}
          <label
            className="flex items-start gap-3 p-4 rounded-xl border border-[#DCD7CE] bg-[#F8F7F4] cursor-pointer select-none hover:border-[#7A2D2F] transition-colors"
            data-testid="custom-work-wrapper"
          >
            <input
              type="checkbox"
              checked={customWork}
              onChange={(e) => setCustomWork(e.target.checked)}
              className="mt-1 h-4 w-4 accent-[#7A2D2F]"
              data-testid="input-custom-work"
            />
            <span className="text-sm text-[#1A1A1A]">
              <span className="font-semibold">Steps or custom work required</span>
              <span className="block text-[#4A4D4A] mt-0.5">
                Tick this if your deck will include steps, borders, handrails,
                raised framing, or demolition of an existing deck.
              </span>
            </span>
          </label>

          <button
            onClick={onCompute}
            className="mt-6 inline-flex items-center gap-2 bg-[#7A2D2F] text-white rounded-full px-6 py-3.5 text-[15px] font-semibold hover:bg-[#0D0D0E] transition-all hover:-translate-y-0.5 shadow-sm"
            data-testid="btn-calculate"
          >
            Show my estimate
            <ArrowRight size={16} />
          </button>
          {error && (
            <p
              className="mt-3 text-sm text-[#5E2023]"
              role="alert"
              data-testid="calc-error"
            >
              {error}
            </p>
          )}
        </div>

        {/* Result */}
        <div
          id="calc-result"
          className="lg:col-span-2 bg-[#F3E8E5] p-7 md:p-10 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-[#DCD7CE]"
          data-testid="calc-result"
        >
          <div>
            <div className="text-[12px] uppercase tracking-[0.25em] text-[#7A2D2F]/70 font-semibold mb-3">
              Estimated project range
            </div>

            {revealed && computed ? (
              <>
                <div
                  className="text-[#0D0D0E] leading-none tracking-tight"
                  style={{ fontFamily: "'Fraunces', serif" }}
                  data-testid="calc-result-range"
                >
                  <div className="text-4xl md:text-5xl font-medium">
                    {AUD.format(computed.low)}
                    <span className="text-[#4A4D4A] font-light mx-2">–</span>
                    {AUD.format(computed.high)}
                  </div>
                </div>
                <div
                  className="mt-3 text-sm text-[#4A4D4A]"
                  data-testid="calc-result-area"
                >
                  Based on{" "}
                  <span className="font-semibold text-[#1A1A1A]">
                    {computed.area.toFixed(1)} m²
                  </span>{" "}
                  of {PRICING[material].label.toLowerCase()}.
                </div>
              </>
            ) : (
              <div className="text-[#4A4D4A] text-[15px] leading-relaxed">
                {computed
                  ? "Click \u201CShow my estimate\u201D below — we’ll just need your phone & email so we can send you a follow-up quote."
                  : `Enter your measurements on the left to see an instant estimated project range for your ${PRICING[material].label.toLowerCase()}.`}
              </div>
            )}

            <p
              className="text-xs text-[#4A4D4A] italic mt-5 leading-relaxed"
              data-testid="calc-disclaimer"
            >
              This is a guide only. Final pricing may vary depending on site
              access, ground condition, height, steps, custom design, waste
              removal, and final measurements.
            </p>

            {customWork && (
              <div
                className="mt-4 flex items-start gap-2 bg-white rounded-xl p-3 border border-[#DCD7CE]"
                data-testid="custom-work-note"
              >
                <Info size={16} className="mt-0.5 text-[#C9A876] shrink-0" />
                <p className="text-xs text-[#1A1A1A] leading-relaxed">
                  Steps, borders, handrails, raised framing, demolition, or
                  other custom work may increase the final price.
                </p>
              </div>
            )}
          </div>

          <div className="mt-7 flex flex-col gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#7A2D2F] text-white rounded-full px-6 py-3.5 text-[15px] font-semibold hover:bg-[#0D0D0E] transition-all hover:-translate-y-0.5"
              data-testid="btn-request-quote"
            >
              Request exact quote <ArrowRight size={16} />
            </Link>
            <a
              href={`tel:${BUSINESS.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-white border border-[#7A2D2F] text-[#7A2D2F] rounded-full px-6 py-3.5 text-[15px] font-semibold hover:bg-[#7A2D2F] hover:text-white transition-all"
              data-testid="btn-call"
            >
              <Phone size={16} />
              Call Woodstone Construction
            </a>

            <div className="mt-3 grid gap-1.5 text-xs text-[#4A4D4A]">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-[#7A2D2F]" />
                Honest, no-pressure quoting
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-[#7A2D2F]" />
                Premium materials, clean finish
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-[#7A2D2F]" />
                Melbourne-based tradespeople
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============= LEAD GATE MODAL ============= */}
      {showLeadGate && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !leadLoading && setShowLeadGate(false)}
          data-testid="lead-gate-modal"
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 pt-6">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-2xl bg-[#F3E8E5] flex items-center justify-center">
                  <Lock size={18} className="text-[#7A2D2F]" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A876] font-semibold">
                    One quick step
                  </div>
                  <div
                    className="text-xl text-[#1A1A1A]"
                    style={{ fontFamily: "'Fraunces', serif" }}
                  >
                    See your estimate
                  </div>
                </div>
              </div>
              <button
                onClick={() => !leadLoading && setShowLeadGate(false)}
                className="p-1.5 text-[#4A4D4A] hover:text-[#7A2D2F]"
                aria-label="Close"
                data-testid="lead-gate-close"
                disabled={leadLoading}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={submitLead} className="p-6">
              <p className="text-sm text-[#4A4D4A] mb-5">
                Pop in your details and we’ll show your estimated project
                range. We’ll also follow up with a more accurate quote.
              </p>

              <div className="space-y-3">
                <div>
                  <label className="block text-[13px] font-medium text-[#1A1A1A] mb-1.5">
                    Name
                  </label>
                  <input
                    value={leadForm.name}
                    onChange={(e) =>
                      setLeadForm((f) => ({ ...f, name: e.target.value }))
                    }
                    placeholder="Your full name"
                    className={inputCls}
                    data-testid="lead-gate-name"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#1A1A1A] mb-1.5">
                    Phone
                  </label>
                  <input
                    value={leadForm.phone}
                    onChange={(e) =>
                      setLeadForm((f) => ({ ...f, phone: e.target.value }))
                    }
                    placeholder="04xx xxx xxx"
                    className={inputCls}
                    data-testid="lead-gate-phone"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#1A1A1A] mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    value={leadForm.email}
                    onChange={(e) =>
                      setLeadForm((f) => ({ ...f, email: e.target.value }))
                    }
                    placeholder="you@example.com"
                    className={inputCls}
                    data-testid="lead-gate-email"
                  />
                </div>
              </div>

              {leadError && (
                <p
                  className="mt-3 text-sm text-[#5E2023]"
                  role="alert"
                  data-testid="lead-gate-error"
                >
                  {leadError}
                </p>
              )}

              <button
                type="submit"
                disabled={leadLoading}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#7A2D2F] text-white rounded-full px-6 py-3.5 font-semibold hover:bg-[#0D0D0E] transition-all disabled:opacity-60"
                data-testid="lead-gate-submit"
              >
                {leadLoading ? "Sending…" : "Show my estimate"}
                {!leadLoading && <ArrowRight size={16} />}
              </button>

              <p className="mt-3 text-[11px] text-[#9A9A90] text-center leading-relaxed">
                Your details are only used to follow up about your project —
                see our{" "}
                <Link
                  to="/privacy"
                  className="underline hover:text-[#7A2D2F]"
                  data-testid="lead-gate-privacy-link"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
